import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { format } from 'date-fns';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { Response } from '../../../../../_shared/models';
import { Pagination } from '../../../../../_shared/models/pagination-response.model';
import { IncidentSeverity, IncidentSeverityLabelKeys } from '../../enums/incident-severity.enum';
import { IncidentReport } from '../../models/accidents-and-incidents-response.model';
import {
  AccidentsAndIncidentsRequest,
  IncidentExportFormat,
  IncidentExportRequest
} from '../../models/accidents-and-incidents-request.model';
import { AccidentsAndIncidentsService } from '../../services/accidents-and-incidents.service';
import { AccidentTypeFilter, AccidentTypeSeverityTypes } from '../../enums/accident-type-filter.enum';
import {
  ATTACHMENTS_VIEWER_MODAL_OPTIONS,
  FORM_MODAL_OPTIONS,
  INCIDENT_DETAILS_MODAL_OPTIONS
} from '../../constants/accidents-and-incidents.constants';
import { EditIncidentModalComponent } from '../modals/edit-incident-modal/edit-incident-modal.component';
import { ClaimInsuranceModalComponent } from '../modals/claim-insurance-modal/claim-insurance-modal.component';
import { IncidentDetailsModalComponent } from '../modals/incident-details-modal/incident-details-modal.component';
import { AttachmentsPillComponent } from '../attachments-pill/attachments-pill.component';
import { AccidentsTableBase } from '../accidents-table-base';
import { PhotosVideosViewerModalComponent } from '../../../../_shared/components/modals/photos-videos-viewer-modal/photos-videos-viewer-modal.component';

@Component({
  selector: 'app-incident-report',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    PaginationComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    AttachmentsPillComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './incident-report.component.html',
  styleUrl: './incident-report.component.scss'
})
export class IncidentReportComponent extends AccidentsTableBase<IncidentReport> implements OnInit, OnDestroy {
  @Input() selectedAccidentType: AccidentTypeFilter = AccidentTypeFilter.All;

  private accidentsAndIncidentsService: AccidentsAndIncidentsService = inject(AccidentsAndIncidentsService);
  private modalService: NgbModal = inject(NgbModal);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  readonly incidentSeverity = IncidentSeverity;
  readonly severityLabelKeys = IncidentSeverityLabelKeys;
  private readonly exportFilePrefix = 'IncidentDetails';
  private readonly exportTimestampFormat = 'yyyy-MM-dd HH-mm-ss';

  isExporting: boolean = false;
  private exportCancelSubject: Subject<void> = new Subject<void>();

  override ngOnInit(): void {
    super.ngOnInit();

    this.accidentsAndIncidentsService.incidentsChanged$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => void this.refresh());
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.exportCancelSubject.next();
    this.exportCancelSubject.complete();
  }

  async onExport(extension: IncidentExportFormat): Promise<void> {
    if (this.isExporting || !this.hasDateRange) {
      return;
    }

    this.isExporting = true;

    try {
      const blob = await this.accidentsAndIncidentsService.exportIncidentReports(
        this.getIncidentExportRequest(),
        extension,
        this.exportCancelSubject
      );

      if (blob.type?.includes('application/json')) {
        const text = await blob.text();
        const errorResponse = JSON.parse(text);
        this.isExporting = false;
        this.errorHandlerService.handleError({ error: errorResponse }, { showToast: true });
        return;
      }

      this.downloadExport(blob, extension);
      this.isExporting = false;
    } catch (error) {
      this.isExporting = false;
      this.errorHandlerService.handleError(error, { showToast: true });
    }
  }

  onEdit(report: IncidentReport): void {
    // The table reloads through incidentsChanged$ once the edit is saved
    const modalRef = this.modalService.open(EditIncidentModalComponent, FORM_MODAL_OPTIONS);
    modalRef.componentInstance.incident = report;
  }

  onClaimInsurance(report: IncidentReport): void {
    const modalRef = this.modalService.open(ClaimInsuranceModalComponent, {
      ...FORM_MODAL_OPTIONS,
      windowClass: 'claim-insurance-modal-window'
    });
    modalRef.componentInstance.title = report.assetName;
    modalRef.componentInstance.assetId = report.assetId;
    modalRef.componentInstance.incidentId = report.incidentId;
    modalRef.componentInstance.categoryId = report.incidentTypeId;
    modalRef.result.then(
      () => this.reloadAfterRowRemoved(),
      () => {}
    );
  }

  onViewLocation(report: IncidentReport): void {
    const modalRef = this.modalService.open(IncidentDetailsModalComponent, INCIDENT_DETAILS_MODAL_OPTIONS);
    modalRef.componentInstance.title = report.assetName;
    modalRef.componentInstance.latitude = report.latitude;
    modalRef.componentInstance.longitude = report.longitude;
    modalRef.componentInstance.locationText = report.location;
  }

  canViewAttachments(report: IncidentReport): boolean {
    return (report.attachments?.length ?? 0) > 0;
  }

  onViewAttachments(report: IncidentReport): void {
    if (!this.canViewAttachments(report)) {
      return;
    }

    const modalRef = this.modalService.open(PhotosVideosViewerModalComponent, ATTACHMENTS_VIEWER_MODAL_OPTIONS);
    modalRef.componentInstance.title = this.transloco.translate('accidents.incidentReport.attachmentsModalTitle');
    modalRef.componentInstance.contextLabel = report.assetName;
    modalRef.componentInstance.reportedBy = report.reportedBy;
    modalRef.componentInstance.reportedAt = report.reportedAt;
    modalRef.componentInstance.reportedByProfilePictureUrl = report.reportedByProfilePictureUrl ?? null;
    modalRef.componentInstance.items = report.attachments ?? [];
  }

  normalizeSeverity(severity: IncidentSeverity | number | string): IncidentSeverity | null {
    if (typeof severity === 'number') {
      return severity as IncidentSeverity;
    }

    const numericSeverity = Number(severity);
    if (!Number.isNaN(numericSeverity)) {
      return numericSeverity as IncidentSeverity;
    }

    switch (severity) {
      case 'LowImpact':
      case 'Minor':
        return IncidentSeverity.LowImpact;
      case 'MediumImpact':
      case 'Intermediate':
        return IncidentSeverity.MediumImpact;
      case 'HighImpact':
      case 'Major':
        return IncidentSeverity.HighImpact;
      default:
        return null;
    }
  }

  getSeverityClass(severity: IncidentSeverity | number | string): string {
    switch (this.normalizeSeverity(severity)) {
      case IncidentSeverity.HighImpact:
        return 'severity-major';
      case IncidentSeverity.MediumImpact:
        return 'severity-intermediate';
      case IncidentSeverity.LowImpact:
        return 'severity-minor';
      default:
        return '';
    }
  }

  getSeverityLabelKey(severity: IncidentSeverity | number | string): string {
    const normalizedSeverity = this.normalizeSeverity(severity);
    return normalizedSeverity ? this.severityLabelKeys[normalizedSeverity] : '';
  }

  protected fetchPage(request: AccidentsAndIncidentsRequest, cancellation: Subject<void>): Promise<Response<Pagination<IncidentReport[]>>> {
    return this.accidentsAndIncidentsService.getIncidentReports({
      ...request,
      severityTypes: this.getSelectedSeverityTypes()
    }, cancellation);
  }

  private getSelectedSeverityTypes(): IncidentSeverity[] {
    return AccidentTypeSeverityTypes[this.selectedAccidentType];
  }

  private getIncidentExportRequest(): IncidentExportRequest {
    return {
      accountIds: this.widgetFilter.accountIds ?? [],
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
      searchTerm: this.trimmedSearchTerm,
      severityTypes: this.getSelectedSeverityTypes(),
      timezoneOffset: new Date().getTimezoneOffset()
    };
  }

  private downloadExport(blob: Blob, extension: IncidentExportFormat): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${this.exportFilePrefix}_${format(new Date(), this.exportTimestampFormat)}.${extension}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}